import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DealTask, TaskPriority } from '@/types/dealTracker';
import { toast } from 'sonner';

export const useDealTasks = (leadId: string) => {
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['deal-tasks', leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('deal_tasks')
        .select('*')
        .eq('lead_id', leadId)
        .order('is_completed', { ascending: true })
        .order('due_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data as DealTask[];
    },
    enabled: !!leadId,
  });

  const createTask = useMutation({
    mutationFn: async (task: {
      title: string;
      description?: string;
      due_date?: string;
      due_time?: string;
      priority: TaskPriority;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await (supabase as any)
        .from('deal_tasks')
        .insert({ ...task, lead_id: leadId, seller_id: user.id });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tasks', leadId] });
      toast.success('Task added');
    },
    onError: () => toast.error('Failed to add task'),
  });

  const toggleTask = useMutation({
    mutationFn: async ({ id, is_completed }: { id: string; is_completed: boolean }) => {
      const { error } = await (supabase as any)
        .from('deal_tasks')
        .update({ is_completed })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tasks', leadId] });
    },
    onError: () => toast.error('Failed to update task'),
  });

  const updateTask = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<{
      title: string;
      description?: string;
      due_date?: string;
      due_time?: string;
      priority: TaskPriority;
    }>) => {
      // Reset reminder_sent when due_date or due_time changes
      const updateData: any = { ...data };
      if ('due_date' in data || 'due_time' in data) {
        updateData.reminder_sent = false;
      }
      const { error } = await (supabase as any)
        .from('deal_tasks')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tasks', leadId] });
      toast.success('Task updated');
    },
    onError: () => toast.error('Failed to update task'),
  });

  const deleteTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('deal_tasks')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tasks', leadId] });
      toast.success('Task deleted');
    },
    onError: () => toast.error('Failed to delete task'),
  });

  return { tasks, isLoading, createTask, toggleTask, updateTask, deleteTask };
};
